import React from 'react';
import { renderPlainTextWithFormatting } from '../../../../../../utils/LearnTab/slideTextRenderer';
import { tUi } from '../../../../../../../../../utils/dq_lc';
import { HtmlContentColumn, HtmlContentWrapper, SlideTitle } from '../../../../../../styles/LearnTab/slideBase.styles';
import { CaseSectionStack, CaseSection, CaseSectionLabel } from './CaseStudyCard.styles';
import { LabMuted } from './HandsOnLabCard.styles';
import { OverviewChecklist, OverviewToolList } from './PracticeOverviewCard.styles';
import { inline, listOf, textOf, SAFE_URL } from './HandsOnLabCard.comp';

// Practice overview: the first slide of a practice session, telling a novice why it matters, the core idea in
// one sentence, what they will build, how it works, which tools they will use and what they need before starting.
export const PracticeOverviewCard = ({
  slide,
  slideEdits = {},
  showEnglishContent = false,
  highlightWord,
  uiLocale = 'en-US',
}) => {
  const origin = slide?._pbdq_origin || slide || {};

  const rawTitle = textOf(origin.headline) || slide?.SlideTitle || '';
  const titleEdit = slideEdits?.['title'];
  const displayTitle = showEnglishContent ? (titleEdit?.original || rawTitle) : (titleEdit?.edited || rawTitle);
  const howItWorks = listOf(origin.how_it_works).map(textOf).filter(Boolean);
  const youWillNeed = listOf(origin.you_will_need).map(textOf).filter(Boolean);
  const tools = listOf(origin.tools).filter((tool) => textOf(tool?.title) || textOf(tool?.name));

  // [key, content] in display order; key is also the i18n suffix and the data-overview-section value.
  const sections = [
    ['why', inline(origin.why)],
    ['coreIdea', inline(origin.core_idea)],
    ['finalResult', inline(origin.final_result)],
    ['howItWorks', howItWorks.length > 0 && (
      <ol>{howItWorks.map((item, idx) => <li key={idx}>{inline(item)}</li>)}</ol>
    )],
    ['tools', tools.length > 0 && (
      <OverviewToolList>
        {tools.map((tool, idx) => {
          const label = textOf(tool.title) || textOf(tool.name);
          const url = textOf(tool.url);
          return (
            <li key={tool.resource_id || idx} data-overview-tool>
              <strong>
                {SAFE_URL.test(url) ? <a href={url} target="_blank" rel="noopener noreferrer">{label}</a> : label}
              </strong>
              {textOf(tool.role) && <div>{inline(tool.role)}</div>}
              {textOf(tool.why) && <LabMuted>{inline(tool.why)}</LabMuted>}
            </li>
          );
        })}
      </OverviewToolList>
    )],
    ['youWillNeed', youWillNeed.length > 0 && (
      <OverviewChecklist>{youWillNeed.map((item, idx) => <li key={idx}>{inline(item)}</li>)}</OverviewChecklist>
    )],
  ];
  const filledSections = sections.filter(([, content]) => content);
  const fallback = filledSections.length === 0 ? textOf(slide?.SlideContent || origin.SlideContent) : '';

  return (
    <HtmlContentColumn data-practice-overview>
      {displayTitle && (
        <SlideTitle>
          {highlightWord ? highlightWord('title', displayTitle) : displayTitle}
        </SlideTitle>
      )}
      <HtmlContentWrapper>
        {filledSections.length > 0 && (
          <CaseSectionStack>
            {filledSections.map(([key, body]) => (
              <CaseSection key={key} data-overview-section={key}>
                <CaseSectionLabel data-overview-label>{tUi(`slide.overview.${key}`, uiLocale)}</CaseSectionLabel>
                {body}
              </CaseSection>
            ))}
          </CaseSectionStack>
        )}
        {fallback && (
          <div className="content-body" style={{ textAlign: 'left', width: '100%' }}>
            {renderPlainTextWithFormatting(fallback)}
          </div>
        )}
      </HtmlContentWrapper>
    </HtmlContentColumn>
  );
};

export default PracticeOverviewCard;
